import { cn } from "@faire/prototype-playground/lib/utils";
import { Button } from "@faire/slate/components/button";
import React from "react";

import { PADX } from "../constants";
import { type PriceMode } from "../PriceLabel";
import { ProductCard, type ProductCardData } from "../ProductCard";

interface BundleModuleProps {
  title: string;
  /** Explanatory copy framing the bundle (what it is / who it's for). */
  description?: string;
  products: ProductCardData[];
  priceMode: PriceMode;
  ctaLabel?: string;
  /** See `BrandDataset.productImageAspect`. */
  aspectRatio?: "square" | "portrait";
}

/**
 * Bundle / sampler module — a product grid on the page's plain white
 * background (no tinted band, for now — it's a product-grid-type module
 * like Bestsellers/Tried & True, not a collection-style module) with an
 * explanatory header and a one-click "add the whole bundle to cart" CTA.
 * Faire configures the bundle from personalization signals (e.g. a new
 * retailer, new to the brand → a Starter Kit sampler of the range). Unlike
 * the big collection card, this leads with the header + the add-all action:
 * grab the set in one click, or pick off individual items (each tile stays
 * sign-up gated like the rest of the logged-out home).
 */
export const BundleModule: React.FC<BundleModuleProps> = ({
  title,
  products,
  priceMode,
  ctaLabel = "Add bundle to cart",
  aspectRatio,
}) => (
  <div className={cn("py-4 md:py-10 lg:py-[48px] 2xl:py-[80px]", PADX)}>
    <div className="mb-5 flex flex-col gap-3 md:mb-6 md:flex-row md:items-end md:justify-between">
      <p className="type-fs-section-heading text-fs-text-primary">{title}</p>
      <span data-signup-gate className="flex-shrink-0">
        <Button variant="primary">{ctaLabel}</Button>
      </span>
    </div>
    <div className="grid grid-cols-2 gap-x-[var(--grid-gutter,16px)] gap-y-6 md:grid-cols-3 lg:grid-cols-5">
      {products.map((card) => (
        // Plain <div> grid cell — a bare gated <button> as a direct grid item
        // collapses to 0 width (matches FeaturedCollection / BestsellersRow).
        <div key={card.token} className="min-w-0">
          <ProductCard
            product={card}
            priceMode={priceMode}
            aspectRatio={aspectRatio}
          />
        </div>
      ))}
    </div>
  </div>
);
